<!--
 * @Descripttion: 剪贴板历史 - 监听剪贴板新增内容，列表展示/编辑/删除/上屏
 *               支持文本、图片、文件三种类型，合计最多保留 200 条
 *               顶部按「全部/文本/图片/文件/URL/代码/视频/富文本」分类筛选，
 *               分类由 type + text + files + html 现算（见 categoryOf），不改主进程数据格式
 * @version:
 * @Author: geory
 * @Date: 2026-09-18 10:00:00
 * @LastEditors: geory
 * @LastEditTime: 2026-10-16 10:47:00
-->
<template>
    <div class="clipboardWrap">
        <div class="clipboardHeader">
            <div class="clipboardTitle">剪贴板历史<span class="clipboardCount">({{list.length}}/200)</span></div>
            <div v-if="list.length" class="clipboardClearBtn" @click="clearAll">清空</div>
        </div>
        <!-- 分类筛选：默认「全部」，点一下只看这一类。没有记录的类别压暗但仍可点，
             点进去能看到"该分类暂无记录"，比直接藏起来更好找 -->
        <div class="clipboardCats" ref="cats">
            <div v-for="c in categories" :key="c.key" class="clipboardCat"
                :class="{'clipboardCatActive': category === c.key, 'clipboardCatEmpty': !categoryCount(c.key)}"
                :title="c.label + ' ' + categoryCount(c.key) + ' 条'" @click="category = c.key">
                <span>{{c.label}}</span>
                <span v-if="categoryCount(c.key)" class="clipboardCatCount">{{categoryCount(c.key)}}</span>
            </div>
        </div>
        <div class="clipboardScroll" :style="{'height': scrollHeight + 'px'}">
            <div v-if="!list.length" class="clipboardEmpty">
                暂无记录，复制文字、图片或文件后会自动记录到这里
            </div>
            <div v-else-if="!filteredList.length" class="clipboardEmpty">
                「{{categoryLabel}}」分类下暂无记录
            </div>
            <div v-for="item in filteredList" :key="item.id" class="clipboardItem">
                <!-- 文本：单行截断。URL / 代码 / 富文本也是文本内容，都走这个分支
                     （富文本条目在没有 html 时退回显示 text） -->
                <div v-if="displayKind(item) === 'text'" class="clipboardItemText" :title="textOf(item)"
                    @click="paste(item)">{{textOf(item)}}</div>

                <!-- 图片：撑满卡片宽度的大图，点击直接粘贴 -->
                <div v-else-if="displayKind(item) === 'image'" class="clipboardItemImage" @click="paste(item)">
                    <img v-if="!imageBroken[item.id]" class="clipboardImage" :src="imageSrc(item)" alt="剪贴板图片"
                        @error="onImageError(item)" />
                    <div v-else class="clipboardImageBroken">图片已失效</div>
                </div>

                <!-- 文件：一条记录里可能有多个文件。
                     只有一条时整卡可点；有多条时每一项单独可点 —— 点哪个就只发哪个，
                     不会把一次复制的所有文件整批发出去 -->
                <div v-else class="clipboardItemFile">
                    <div v-if="singleFile(item)" class="clipboardFileRow" @click.stop="pasteFile(item, item.files[0])">
                        <img class="clipboardFileIcon" :src="getFileIcon(firstFile(item))" alt="文件" />
                        <div class="clipboardFileInfo">
                            <div class="clipboardFileName" :title="fileTitle(item)">{{fileTitle(item)}}</div>
                            <div class="clipboardFileMeta">{{fileMeta(item)}}</div>
                        </div>
                    </div>
                    <template v-else>
                        <!-- 图片用缩略图格子，一眼能看出是哪张 -->
                        <div v-if="fileThumbs(item).length" class="clipboardFileThumbs">
                            <div v-for="(f, i) in fileThumbs(item)" :key="f.path || ('t' + i)"
                                class="clipboardFileThumb" :title="f.name" @click.stop="pasteFile(item, f)">
                                <img :src="f.thumbUrl" alt="图片" @error="onThumbError" />
                                <div v-if="i === fileThumbs(item).length - 1 && fileThumbMore(item) > 0"
                                    class="clipboardFileThumbMore">+{{fileThumbMore(item)}}</div>
                            </div>
                        </div>
                        <!-- 其余文件（含超出缩略图上限的）一行一个，保证每个都能点到 -->
                        <div v-for="(f, i) in fileRows(item)" :key="f.path || ('r' + i)" class="clipboardFileRow"
                            :title="f.name" @click.stop="pasteFile(item, f)">
                            <img class="clipboardFileIcon" :src="getFileIcon(f.name)" alt="文件" />
                            <div class="clipboardFileInfo">
                                <div class="clipboardFileName">{{f.name}}</div>
                                <div class="clipboardFileMeta">{{fileItemMeta(f)}}</div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="clipboardItemBottom">
                    <div class="clipboardItemTime" @click="pasteWhole(item)">{{formatTime(item.ts)}}</div>
                    <div class="clipboardItemOps">
                        <span class="clipboardOp clipboardOpAdd"
                            @click.stop="addToHuashu(item)">添加到话术</span>
                        <!-- 图片/文件没有文本内容可以编辑，只给文本留编辑入口 -->
                        <span v-if="itemType(item) === 'text'" class="clipboardOp"
                            @click.stop="openEdit(item)">编辑</span>
                        <span class="clipboardOp clipboardOpDel" @click.stop="remove(item)">删除</span>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog title="编辑剪贴板内容" :visible.sync="editShow" width="460px" :append-to-body="true"
            custom-class="clipboardEditDialog" :close-on-click-modal="false">
            <el-input type="textarea" :rows="6" v-model="editText" placeholder="请输入内容"></el-input>
            <div slot="footer" class="clipboardDialogFooter">
                <el-button @click="editShow = false">取 消</el-button>
                <el-button type="primary" @click="saveEdit">保 存</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
    import Vue from 'vue'
    import {
        Dialog,
        Button,
        Input
    } from 'element-ui';
    Vue.use(Dialog)
    Vue.use(Button)
    Vue.use(Input)

    // 顶部分类：顺序即展示顺序，key === 'all' 表示不筛选
    const CATEGORIES = [{
        key: 'all',
        label: '全部'
    }, {
        key: 'text',
        label: '文本'
    }, {
        key: 'image',
        label: '图片'
    }, {
        key: 'file',
        label: '文件'
    }, {
        key: 'url',
        label: 'URL'
    }, {
        key: 'code',
        label: '代码'
    }, {
        key: 'video',
        label: '视频'
    }, {
        key: 'richtext',
        label: '富文本'
    }]

    // 视频后缀：复制一个视频文件归到「视频」，不再混在「文件」里
    const VIDEO_EXTS = ['mp4', 'm4v', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'f4v', 'webm',
        'mpeg', 'mpg', '3gp', '3g2', 'rmvb', 'rm', 'asf', 'vob', 'ogv', 'm2ts', 'mts', 'divx'
    ]

    // 「文本是不是一个网址」用到的后缀表：
    // 带协议（http/https/ftp/file）或 www. 的直接认；裸域名要求后缀在这张表里，
    // 否则 index.js / v1.2 / 3.14 这类会被误判成 URL
    const URL_TLDS = ['com', 'cn', 'net', 'org', 'edu', 'gov', 'int', 'mil', 'io', 'ai', 'co',
        'me', 'tv', 'cc', 'info', 'biz', 'top', 'xyz', 'site', 'online', 'shop', 'app', 'dev',
        'us', 'uk', 'jp', 'hk', 'tw', 'kr', 'sg', 'my', 'th', 'vn', 'in', 'au', 'ca', 'de',
        'fr', 'ru', 'es', 'it', 'nl', 'se', 'ch', 'nz', 'mx', 'id', 'ph', 'tr', 'pl', 'be',
        'at', 'dk', 'no', 'fi', 'cz', 'gr', 'pt', 'ie', 'il', 'ae', 'sa', 'za', 'br', 'ar',
        'cl', 'pe', 'live', 'tech', 'store', 'work', 'group', 'art', 'fun', 'vip', 'club'
    ]

    // 「文本是不是代码」的打分表：命中即累加权重，总分 >= 3 视为代码。
    // 有意只做保守判断 —— 宁可把一段脚本留在「文本」里，也不要把中文闲聊塞进「代码」
    const CODE_PATTERNS = [
        // 语言关键字起行。后面必须跟代码样子，避免 "from now on" 这类英文句子误判
        { re: /(^|\n)[ \t]*(?:function|class|interface|struct|enum|def|func)\s+\w+/, w: 3 },
        { re: /(^|\n)[ \t]*(?:const|let|var)\s+[\w${\[]/, w: 3 },
        { re: /(^|\n)[ \t]*import\s+[\w{*'"]/, w: 3 },
        { re: /(^|\n)[ \t]*from\s+[\w.'"]+\s+import\s+/, w: 3 },
        { re: /(^|\n)[ \t]*export\s+(?:default|const|function|class|\{)/, w: 3 },
        { re: /(^|\n)[ \t]*package\s+[\w.]+;?/, w: 3 },
        { re: /(^|\n)[ \t]*using\s+[\w:]+/, w: 3 },
        { re: /(^|\n)[ \t]*#(?:include|define|import|pragma)\b/, w: 3 },
        // 控制流/输出语句/SQL/HTML 标签
        { re: /\b(?:if|for|while|switch|catch)\s*\([^)]*\)\s*\{/, w: 3 },
        { re: /\b(?:console\.(?:log|error|warn|info)|System\.out\.print\w*|fmt\.Print\w*)\s*\(/, w: 3 },
        { re: /(^|\n)[ \t]*(?:print|printf|println|echo|puts)\s*[\( ]/, w: 3 },
        { re: /\b(?:SELECT|INSERT\s+INTO|UPDATE\s+\w+\s+SET|DELETE\s+FROM|CREATE\s+TABLE|ALTER\s+TABLE)\b/i, w: 3 },
        { re: /\b(?:FROM|WHERE|JOIN|VALUES|GROUP\s+BY|ORDER\s+BY)\b/, w: 1 },
        { re: /<\/(?:div|span|p|a|html|head|body|script|style|ul|ol|li|table|tr|td|th|section|article|header|footer|h[1-6])>/i, w: 3 },
        { re: /<\/?[A-Za-z][\w:-]*(?:\s[^<>]*)?\/?>/, w: 1 },
        // 代码符号与方法调用
        { re: /=>|::|->|\?\?|\?\./, w: 2 },
        { re: /\w+(?:\.\w+)+\([^()]*\)/, w: 2 },
        // JSON 整块：整段就是一个 {..} / [..]
        { re: /^\s*[\[{][\s\S]*[\]}]\s*$/, w: 2 },
        { re: /["'][\w-]+["']\s*:/, w: 2 },
        // 弱特征：赋值行 / 花括号 / 行尾分号 / 缩进 / 注释行
        { re: /(^|\n)[ \t]*[\w$.\[\]'"]+[ \t]*[:=][ \t]*\S/, w: 1 },
        { re: /[{}]/, w: 1 },
        { re: /;\s*(?:\n|$)/, w: 1 },
        { re: /(^|\n)[ \t]{2,}\S/, w: 1 },
        { re: /(^|\n)[ \t]*(?:\/\/|#|\/\*|\*|--)[ \t]*\S/, w: 1 }
    ]

    // 富文本条目可能带的内容字段（主进程给 html/rtf 时才有；老数据命中不了）
    const RICHTEXT_FIELDS = ['html', 'htmlText', 'rtf', 'rtfText', 'richText']
    const RICHTEXT_TYPES = ['richtext', 'richText', 'html', 'rtf']

    export default {
        name: 'ClipboardHistory',
        props: {
            height: {
                type: Number,
                default: 400
            }
        },
        data() {
            return {
                list: [],
                categories: CATEGORIES,
                category: 'all', // 当前选中的分类 key
                catsHeight: 0, // 分类栏真实高度（换行后行数会变，量出来更稳）
                editShow: false,
                editId: null,
                editText: '',
                _onUpdate: null,
                _onError: null,
                _measureTimer: null,
                _onResize: null,
                thumbFailed: {}, // 缩略图加载失败 -> 退回加载原图
                imageBroken: {} // 原图也加载不出来 -> 显示失效占位
            }
        },
        computed: {
            scrollHeight() {
                // 头部（约 56px）+ 分类栏（实测高度）之外的剩余空间都给滚动区
                return Math.max(120, (this.height || 400) - 60 - this.catsHeight)
            },
            // 每条记录归到哪个分类：只算一次，后面的筛选和计数都从它派生
            categorized() {
                return (this.list || []).map((item) => ({
                    item: item,
                    cat: this.categoryOf(item)
                }))
            },
            counts() {
                const c = {
                    all: (this.list || []).length
                }
                this.categorized.forEach((e) => {
                    c[e.cat] = (c[e.cat] || 0) + 1
                })
                return c
            },
            filteredList() {
                if (this.category === 'all') return this.list || []
                return this.categorized.filter((e) => e.cat === this.category).map((e) => e.item)
            },
            categoryLabel() {
                const c = CATEGORIES.find((it) => it.key === this.category)
                return c ? c.label : ''
            }
        },
        watch: {
            // 面板高度变了要重新算滚动区；分类栏高度也可能跟着换行数变
            height() {
                this.measureCats()
            }
        },
        mounted() {
            this.measureCats()
            this._onResize = () => {
                if (this._measureTimer) clearTimeout(this._measureTimer)
                this._measureTimer = setTimeout(() => this.measureCats(), 100)
            }
            window.addEventListener('resize', this._onResize)
        },
        created() {
            this.load()
            const r = this.ipc()
            if (r) {
                this._onUpdate = (_event, data) => {
                    this.list = Array.isArray(data) ? data : []
                    // 列表整批换新后，上一批的加载失败记录没有意义了
                    this.thumbFailed = {}
                    this.imageBroken = {}
                    this.measureCats()
                }
                r.on('clipboard-history-updated', this._onUpdate)
                // 主进程发现这条记录已经不可用（源文件被删/移动、剪贴板写入失败）
                this._onError = (_event, message) => {
                    this.$message({
                        message: message || '这条记录已不可用',
                        type: 'warning'
                    })
                }
                r.on('clipboard-history-error', this._onError)
            }
        },
        beforeDestroy() {
            if (this._measureTimer) clearTimeout(this._measureTimer)
            if (this._onResize) window.removeEventListener('resize', this._onResize)
            const r = this.ipc()
            if (!r) return
            try {
                if (this._onUpdate) r.removeListener('clipboard-history-updated', this._onUpdate)
                if (this._onError) r.removeListener('clipboard-history-error', this._onError)
            } catch (_) {}
        },
        methods: {
            isElectron() {
                return !!(this.$common && this.$common.isElectron && this.$common.isElectron())
            },
            ipc() {
                if (!this.isElectron()) return null
                try {
                    return window.require('electron').ipcRenderer
                } catch (_) {
                    return null
                }
            },
            async load() {
                const r = this.ipc()
                if (!r) return
                try {
                    const data = await r.invoke('clipboard-history-get')
                    this.list = Array.isArray(data) ? data : []
                } catch (_) {
                    this.list = []
                }
                // 重新拉一次列表后，上一轮"图片加载失败"的记录不该继续压着这张图
                this.thumbFailed = {}
                this.imageBroken = {}
                // 分类计数变了会让分类栏换行，高度要重新量
                this.measureCats()
            },
            refresh() {
                this.load()
            },
            // 老数据没有 type 字段，统一按文本处理
            itemType(item) {
                return (item && item.type) || 'text'
            },
            // 卡片按内容形态分三种画法：文本 / 图片 / 文件。
            // 分类比画法多（URL、代码、富文本都是文本形态），这样新增分类不会画出空卡片
            displayKind(item) {
                if (this.itemType(item) === 'image') return 'image'
                if (((item && item.files) || []).length) return 'file'
                return 'text'
            },
            // 卡片上显示的文本：优先 text，富文本条目没有 text 时把 html 扒成纯文本
            textOf(item) {
                const text = (item && item.text) || ''
                if (String(text).trim()) return text
                const html = (item && (item.html || item.htmlText)) || ''
                // 只做最朴素的去标签 + 常见实体还原，够列表里认内容就行
                return String(html)
                    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/&nbsp;/gi, ' ')
                    .replace(/&amp;/gi, '&')
                    .replace(/&lt;/gi, '<')
                    .replace(/&gt;/gi, '>')
                    .replace(/&quot;/gi, '"')
                    .replace(/\s+/g, ' ')
                    .trim()
            },
            // 分类栏高度实测（宽度不足时会折成两行，写死高度会算错滚动区）
            measureCats() {
                this.$nextTick(() => {
                    const el = this.$refs.cats
                    if (!el) return
                    // +8 是分类栏自身的下边距
                    const h = el.offsetHeight + 8
                    if (h !== this.catsHeight) this.catsHeight = h
                })
            },
            categoryCount(key) {
                return this.counts[key] || 0
            },
            // ── 分类判定 ──
            // 主进程只落 text / image / file 三种 type，URL/代码/视频/富文本 都在这里现算：
            //  · 富文本：主进程给出 html/rtf 内容时命中（老数据与纯文本不会命中）
            //  · 图片：type === image
            //  · 视频：文件记录里的文件全是视频后缀
            //  · URL / 代码：在文本内容上做特征判断，都不像就是「文本」
            // 显式 type 优先：主进程以后自己分类了，这里不用改
            categoryOf(item) {
                if (!item) return 'text'
                const type = item.type
                if (RICHTEXT_TYPES.indexOf(type) >= 0 || this.hasRichText(item)) return 'richtext'
                if (type === 'image') return 'image'
                const files = item.files || []
                if (type === 'file' || (type !== 'text' && files.length)) {
                    return this.isVideoItem(files) ? 'video' : 'file'
                }
                if (type === 'url') return 'url'
                if (type === 'code') return 'code'
                const text = String(item.text || '')
                if (this.isUrlText(text)) return 'url'
                if (this.isCodeText(text)) return 'code'
                return 'text'
            },
            hasRichText(item) {
                return RICHTEXT_FIELDS.some((k) => !!(item && item[k]))
            },
            // 一个文件记录算不算「视频」：记录里的文件都有视频后缀才算，
            // 混着图片/文档时仍然归「文件」，免得只看第一条把整条记歪
            isVideoItem(files) {
                if (!files || !files.length) return false
                return files.every((f) => {
                    const name = (f && (f.name || f.path)) || ''
                    const ext = String(name).split('.').pop().toLowerCase()
                    return name.indexOf('.') > 0 && VIDEO_EXTS.indexOf(ext) >= 0
                })
            },
            // 整段不带空白、且是协议地址/www./已知后缀的域名，才算 URL。
            // 一句话里夹着链接（或一次复制多个链接）按普通文本处理，避免把整段聊天记成网址
            isUrlText(text) {
                const t = String(text || '').trim()
                if (!t || /\s/.test(t)) return false
                if (/^(?:https?|ftp|file):\/\/\S+$/i.test(t)) return true
                if (/^www\.[\w-]+(?:\.[\w-]+)+\S*$/i.test(t)) return true
                const m = t.match(/^[\w-]+(?:\.[\w-]+)*\.([A-Za-z]{2,})(?:[/?#]\S*)?$/)
                return !!(m && URL_TLDS.indexOf(m[1].toLowerCase()) >= 0)
            },
            // 代码打分：命中即加权，>= 3 分算代码。见 CODE_PATTERNS 的注释
            isCodeText(text) {
                const t = String(text || '')
                if (!t.trim()) return false
                let score = 0
                for (let i = 0; i < CODE_PATTERNS.length; i++) {
                    if (CODE_PATTERNS[i].re.test(t)) score += CODE_PATTERNS[i].w
                    if (score >= 3) return true
                }
                return false
            },
            paste(item) {
                const r = this.ipc()
                if (!r || !item) return
                // 文本/图片/文件的分支都在主进程的 pasteClipboardItem 里
                r.send('clipboard-history-paste', item.id)
            },
            // 点整张卡片/时间：只有"内容只有一条"的记录才整条粘贴。
            // 一次复制了多个文件时，界面上每一项都单独可点，避免误把全部一起发出去
            pasteWhole(item) {
                if (this.itemType(item) === 'file' && (item.files || []).length > 1) return
                this.paste(item)
            },
            // 只发送多文件记录里的某一个文件（点哪张/哪个就发哪个）。
            // 刻意复用 paste 的同一个通道、把文件路径当第二个参数带过去：
            // 这样万一主进程还没重启（还不认识这个参数），点击只是退化成整条发送，
            // 而不是像换一个通道那样点了完全没反应
            pasteFile(item, file) {
                const r = this.ipc()
                if (!r || !item || !file || !file.path) return
                r.send('clipboard-history-paste', item.id, file.path)
            },
            // 一键添加到话术：由 Home 弹出团队选择框，选完团队/分类后再打开添加话术窗口。
            // 文本走 prefillText，图片/文件走 prefillMedia（主进程只把落盘路径带过去）
            addToHuashu(item) {
                if (!item) return
                const kind = this.displayKind(item)
                // URL / 代码 / 富文本本质上都是文本，话术窗口只认 text/image/file，
                // 所以这里统一归一成 {type:'text', text} 再发出去（Home 那边不用改）
                if (kind === 'text') {
                    const text = this.textOf(item)
                    if (!text) return
                    this.$emit('add-to-huashu', Object.assign({}, item, {
                        type: 'text',
                        text: text
                    }))
                    return
                }
                if (kind === 'image' && !item.imagePath) return
                if (kind === 'file' && !(item.files || []).length) return
                this.$emit('add-to-huashu', item)
            },
            openEdit(item) {
                this.editId = item.id
                this.editText = item.text
                this.editShow = true
            },
            saveEdit() {
                const r = this.ipc()
                if (!r) return
                r.send('clipboard-history-edit', this.editId, this.editText)
                this.editShow = false
            },
            remove(item) {
                const r = this.ipc()
                if (!r || !item) return
                const tip = this.itemType(item) === 'image' ?
                    '确定删除这张图片吗？' : (this.itemType(item) === 'file' ?
                        '确定删除这条文件记录吗？（不会删除本地文件）' : '确定删除这条记录吗？')
                this.$confirm(tip, '提示', {
                    type: 'warning'
                }).then(() => {
                    r.send('clipboard-history-delete', item.id)
                }).catch(() => {})
            },
            clearAll() {
                const r = this.ipc()
                if (!r) return
                this.$confirm('确定清空全部剪贴板历史吗？清空后记录的图片也会一并删除。', '提示', {
                    type: 'warning'
                }).then(() => {
                    r.send('clipboard-history-clear')
                }).catch(() => {})
            },
            // ── 图片 ──
            imageSrc(item) {
                // 优先用缩略图（列表里同时解码好几张大图会很吃内存），失败后退回原图
                if (this.thumbFailed[item.id]) return item.imageUrl || ''
                return item.thumbUrl || item.imageUrl || ''
            },
            onImageError(item) {
                // 第一次失败退到原图，再失败才认定图片不可用
                if (!this.thumbFailed[item.id] && item.imageUrl &&
                    item.thumbUrl && item.thumbUrl !== item.imageUrl) {
                    this.$set(this.thumbFailed, item.id, true)
                    return
                }
                this.$set(this.imageBroken, item.id, true)
            },
            // ── 文件 ──
            firstFile(item) {
                const f = (item.files || [])[0]
                return (f && (f.name || f.path)) || '未知文件'
            },
            // 这条记录是不是只有一个文件（只有一条时整卡可点，与以前行为一致）
            singleFile(item) {
                return (item.files || []).length === 1
            },
            fileTitle(item) {
                const files = item.files || []
                if (!files.length) return '未知文件'
                const name = files[0].name || files[0].path
                return files.length > 1 ? name + ' 等 ' + files.length + ' 个文件' : name
            },
            fileMeta(item) {
                const files = item.files || []
                if (!files.length) return ''
                if (files.length === 1 && files[0].isDir) return '文件夹'
                const total = files.reduce((sum, f) => sum + ((f && f.size) || 0), 0)
                const sizeText = this.formatSize(total)
                return files.length > 1 ? files.length + ' 个文件 · ' + sizeText : sizeText
            },
            // 单个文件的名称/大小（多个文件逐行展示时每行各自的信息）
            fileItemMeta(f) {
                if (!f) return ''
                if (f.isDir) return '文件夹'
                return this.formatSize(f.size)
            },
            // 文件条目里带预览缩略图的图片文件，用缩略图格子展示。
            // 复制多张图片时列表里要能看出每张是什么，主进程已经为它们各生成了一张缩略图。
            // 最多展示 9 张（与主进程的生成上限一致），多出来的在最后一格显示 +N。
            fileThumbs(item) {
                return (item.files || [])
                    .filter((f) => f && f.thumbUrl)
                    .slice(0, 9)
            },
            fileThumbMore(item) {
                const total = (item.files || []).filter((f) => f && f.thumbUrl).length
                return Math.max(0, total - 9)
            },
            // 没进缩略图格子的那些文件（非图片文件、以及超出上限的）逐行列出，
            // 保证一条记录里每个文件都能单独点到、单独发出去
            fileRows(item) {
                const shown = new Set(this.fileThumbs(item).map((f) => f && f.path))
                return (item.files || []).filter((f) => f && !shown.has(f.path))
            },
            // 缩略图文件被清理掉时（比如用户手删了 userData）不显示破图，留灰底占位
            onThumbError(e) {
                if (e && e.target) e.target.style.visibility = 'hidden'
            },
            formatSize(bytes) {
                const n = Number(bytes) || 0
                if (n < 1024) return n + ' B'
                if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
                if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB'
                return (n / 1024 / 1024 / 1024).toFixed(1) + ' GB'
            },
            // 文件图标：与 Home.vue / HuashuEdit.vue 的 getFileIcon 保持同一套图
            // （项目里没有共用方法，这里是第三份；如需统一应抽到 $common）
            getFileIcon(fileName) {
                const name = (fileName || '').toLowerCase()
                if (name.includes(".doc")) {
                    return "https://imagessl.edujia.com/A-1ed71af7_20230602143256.png"
                } else if (name.includes(".txt")) {
                    return "https://imagessl.edujia.com/A-1ed71af7_20230602143245.png"
                } else if (name.includes(".xls")) {
                    return "https://imagessl.edujia.com/A-1ed71af7_20230602143214.png"
                } else if (name.includes(".mp3") || name.includes(".m4a")) {
                    return "https://imagessl.edujia.com/A-1ed71af7_20230529114041.png"
                } else if (name.includes(".pdf")) {
                    return "https://imagessl.edujia.com/A-1ed71af7_20230529114100.png"
                } else if (name.includes(".ppt") || name.includes(".pptx")) {
                    return "https://imagessl.edujia.com/A-1ed71af7_20230608114321.png"
                }
                return "https://imagessl.edujia.com/A-1ed71af7_20230601152642.png"
            },
            formatTime(ts) {
                if (!ts) return ''
                const d = new Date(ts)
                const pad = (n) => (n < 10 ? '0' + n : '' + n)
                return pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(
                    d.getMinutes())
            }
        }
    }
</script>

<style scoped>
    .clipboardWrap {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        padding: 8px 12px;
        background: #fff;
    }

    .clipboardHeader {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
    }

    .clipboardTitle {
        font-size: 15px;
        font-weight: 600;
        color: #333;
    }

    .clipboardCount {
        margin-left: 6px;
        font-size: 13px;
        font-weight: 400;
        color: #999;
    }

    .clipboardClearBtn {
        font-size: 13px;
        color: #999;
        cursor: pointer;
        padding: 4px 8px;
    }

    .clipboardClearBtn:hover {
        color: #f56c6c;
    }

    /* 分类栏：窄面板放不下时折行，全部类别一直可见（不做横向滚动，免得藏起来找不到） */
    .clipboardCats {
        flex-shrink: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-bottom: 8px;
    }

    .clipboardCat {
        display: flex;
        align-items: center;
        height: 22px;
        padding: 0 9px;
        border: 1px solid #e4e7ed;
        border-radius: 11px;
        background: #f7f8fa;
        font-size: 12px;
        line-height: 1;
        color: #666;
        cursor: pointer;
        user-select: none;
        transition: all 0.15s;
    }

    .clipboardCat:hover {
        border-color: #409eff;
        color: #409eff;
    }

    /* 选中态：实心蓝底，和「清空」的红、条目的操作蓝区分开 */
    .clipboardCatActive,
    .clipboardCatActive:hover {
        border-color: #409eff;
        background: #409eff;
        color: #fff;
    }

    /* 没有记录的类别压暗，但仍然可以点（点进去看空态提示）；
       选中态优先，别把选中那颗也压成灰字 */
    .clipboardCatEmpty:not(.clipboardCatActive) {
        color: #c0c4cc;
    }

    .clipboardCatCount {
        margin-left: 4px;
        font-size: 11px;
        opacity: 0.75;
    }

    .clipboardScroll {
        flex: 1;
        overflow-y: auto;
        min-height: 0;
    }

    .clipboardEmpty {
        margin-top: 60px;
        text-align: center;
        font-size: 13px;
        color: #bbb;
    }

    .clipboardItem {
        display: flex;
        flex-direction: column;
        padding: 10px 12px;
        margin-bottom: 8px;
        border: 1px solid #eee;
        border-radius: 6px;
        transition: background 0.15s;
    }

    .clipboardItem:hover {
        background: #f7f8fa;
        border-color: #e4e7ed;
    }

    /* 第二行：时间在左，操作按钮在右，保持同一行 */
    .clipboardItemBottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        margin-top: 4px;
    }

    .clipboardItemText {
        font-size: 13px;
        color: #333;
        line-height: 1.5;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
    }

    /* 图片：固定尺寸缩略图（160x120），不再撑满卡片宽度 —— 撑满时在宽面板上
       会变成一条很宽的横幅，一屏放不下几条。尺寸想调只改这两个值即可。
       align-self 是为了不被父级 flex 的 stretch 拉宽（父级是 column flex）。 */
    .clipboardItemImage {
        position: relative;
        align-self: flex-start;
        width: 160px;
        height: 120px;
        max-width: 100%;
        border-radius: 4px;
        overflow: hidden;
        background: #f5f6f8;
        cursor: pointer;
    }

    .clipboardImage {
        display: block;
        width: 100%;
        height: 100%;
        /* contain：整张图都能看到，不裁切（截图类图片被裁掉一块会认不出来） */
        object-fit: contain;
    }

    .clipboardImageBroken {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: #bbb;
    }

    /* 文件：可选的多图缩略图网格 + 图标/文件名/大小 */
    .clipboardItemFile {
        display: flex;
        flex-direction: column;
        cursor: pointer;
        overflow: hidden;
    }

    /* 复制多张图片时，铺在文件名上面的一层缩略图，用来看出每张是什么 */
    .clipboardFileThumbs {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-bottom: 8px;
    }

    .clipboardFileThumb {
        position: relative;
        width: 72px;
        height: 72px;
        border-radius: 4px;
        overflow: hidden;
        background: #f5f6f8;
        /* 每一项单独可点：点哪张就只发哪张 */
        cursor: pointer;
    }

    .clipboardFileThumb:hover {
        box-shadow: 0 0 0 2px #409eff inset;
    }

    .clipboardFileThumb img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    /* 超过展示上限时，压在最后一格上的 +N */
    .clipboardFileThumbMore {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        font-size: 14px;
    }

    .clipboardFileRow {
        display: flex;
        align-items: center;
        min-width: 0;
        padding: 2px 0;
        /* 每行单独可点：点哪条就只发哪条 */
        cursor: pointer;
    }

    .clipboardFileRow:hover {
        background: #f0f6ff;
    }

    .clipboardFileIcon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        margin-right: 10px;
    }

    .clipboardFileInfo {
        min-width: 0;
        flex: 1;
    }

    .clipboardFileName {
        font-size: 13px;
        color: #333;
        line-height: 1.4;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .clipboardFileMeta {
        margin-top: 2px;
        font-size: 12px;
        color: #999;
    }

    .clipboardItemTime {
        font-size: 12px;
        color: #bbb;
        cursor: pointer;
        white-space: nowrap;
    }

    .clipboardItemOps {
        flex-shrink: 0;
        margin-left: 12px;
        display: flex;
        align-items: center;
    }

    .clipboardOp {
        font-size: 12px;
        color: #409eff;
        cursor: pointer;
        padding: 2px 6px;
    }

    .clipboardOp:hover {
        color: #66b1ff;
    }

    .clipboardOpDel {
        color: #f56c6c;
    }

    .clipboardOpDel:hover {
        color: #f78989;
    }

    .clipboardOpAdd {
        color: #E6A23C;
    }

    .clipboardOpAdd:hover {
        color: #EBB563;
    }
</style>
